/**
 * MenuBar Component
 * Professional menu bar with dropdown menus
 */

import { useState, useCallback, memo } from 'react'
import Menu from './Menu'
import { MenuItemConfig } from './MenuItem'

export interface MenuBarProps {
  // File menu actions
  onNewDiagram?: () => void
  onOpenJSON?: () => void
  onSaveJSON?: () => void
  onExport?: () => void  // Opens export modal

  // Edit menu actions
  onUndo?: () => void
  onRedo?: () => void
  onCopy?: () => void
  onPaste?: () => void
  onCut?: () => void
  onSelectAll?: () => void
  onDeleteSelected?: () => void
  canUndo?: boolean
  canRedo?: boolean
  hasSelection?: boolean

  // View menu actions
  onToggleNodeLibrary?: () => void
  onTogglePropertiesPanel?: () => void
  onToggleGrid?: () => void
  onFitView?: () => void
  onZoomIn?: () => void
  onZoomOut?: () => void
  showNodeLibrary?: boolean
  showPropertiesPanel?: boolean
  showGrid?: boolean

  // Layout menu actions
  onAutoLayoutHierarchical?: () => void
  onAutoLayoutForce?: () => void
  onAutoLayoutGrid?: () => void
  onAutoLayoutCircular?: () => void
  onAlignLeft?: () => void
  onAlignRight?: () => void
  onAlignTop?: () => void
  onAlignBottom?: () => void
  onDistributeHorizontally?: () => void
  onDistributeVertically?: () => void

  // Help menu actions
  onShowDocumentation?: () => void
  onShowKeyboardShortcuts?: () => void
  onShowAbout?: () => void
}

function MenuBar({
  onNewDiagram,
  onOpenJSON,
  onSaveJSON,
  onExport,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onCut,
  onSelectAll,
  onDeleteSelected,
  canUndo = false,
  canRedo = false,
  hasSelection = false,
  onToggleNodeLibrary,
  onTogglePropertiesPanel,
  onToggleGrid,
  onFitView,
  onZoomIn,
  onZoomOut,
  showNodeLibrary = true,
  showPropertiesPanel = true,
  showGrid = true,
  onAutoLayoutHierarchical,
  onAutoLayoutForce,
  onAutoLayoutGrid,
  onAutoLayoutCircular,
  onAlignLeft,
  onAlignRight,
  onAlignTop,
  onAlignBottom,
  onDistributeHorizontally,
  onDistributeVertically,
  onShowDocumentation,
  onShowKeyboardShortcuts,
  onShowAbout,
}: MenuBarProps) {
  const [openMenu, setOpenMenu] = useState<string | null>(null)

  const handleToggleMenu = useCallback((menuName: string) => {
    setOpenMenu(prev => prev === menuName ? null : menuName)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(null)
  }, [])

  // File Menu
  const fileMenuItems: MenuItemConfig[] = [
    {
      label: 'New Diagram',
      shortcut: 'Ctrl+N',
      action: onNewDiagram,
      icon: '📄',
    },
    {
      label: 'Open JSON',
      shortcut: 'Ctrl+O',
      action: onOpenJSON,
      icon: '📂',
    },
    { type: 'separator' },
    {
      label: 'Save JSON',
      shortcut: 'Ctrl+S',
      action: onSaveJSON,
      icon: '💾',
    },
    { type: 'separator' },
    {
      label: 'Export...',
      shortcut: 'Ctrl+E',
      action: onExport,
      icon: '📤',
    },
  ]

  // Edit Menu
  const editMenuItems: MenuItemConfig[] = [
    {
      label: 'Undo',
      shortcut: 'Ctrl+Z',
      action: onUndo,
      disabled: !canUndo,
    },
    {
      label: 'Redo',
      shortcut: 'Ctrl+Y',
      action: onRedo,
      disabled: !canRedo,
    },
    { type: 'separator' },
    {
      label: 'Cut',
      shortcut: 'Ctrl+X',
      action: onCut,
      disabled: !hasSelection,
    },
    {
      label: 'Copy',
      shortcut: 'Ctrl+C',
      action: onCopy,
      disabled: !hasSelection,
    },
    {
      label: 'Paste',
      shortcut: 'Ctrl+V',
      action: onPaste,
    },
    { type: 'separator' },
    {
      label: 'Select All',
      shortcut: 'Ctrl+A',
      action: onSelectAll,
    },
    {
      label: 'Delete Selected',
      shortcut: 'Delete',
      action: onDeleteSelected,
      disabled: !hasSelection,
    },
  ]

  // View Menu
  const viewMenuItems: MenuItemConfig[] = [
    {
      label: 'Node Library',
      shortcut: 'Ctrl+L',
      action: onToggleNodeLibrary,
      checked: showNodeLibrary,
    },
    {
      label: 'Properties Panel',
      shortcut: 'Ctrl+P',
      action: onTogglePropertiesPanel,
      checked: showPropertiesPanel,
    },
    {
      label: 'Show Grid',
      shortcut: 'Ctrl+G',
      action: onToggleGrid,
      checked: showGrid,
    },
    { type: 'separator' },
    {
      label: 'Fit View',
      shortcut: 'Ctrl+0',
      action: onFitView,
    },
    {
      label: 'Zoom In',
      shortcut: 'Ctrl++',
      action: onZoomIn,
    },
    {
      label: 'Zoom Out',
      shortcut: 'Ctrl+-',
      action: onZoomOut,
    },
  ]

  // Layout Menu
  const layoutMenuItems: MenuItemConfig[] = [
    {
      label: 'Auto Layout: Hierarchical',
      action: onAutoLayoutHierarchical,
    },
    {
      label: 'Auto Layout: Force',
      action: onAutoLayoutForce,
    },
    {
      label: 'Auto Layout: Grid',
      action: onAutoLayoutGrid,
    },
    {
      label: 'Auto Layout: Circular',
      action: onAutoLayoutCircular,
    },
    { type: 'separator' },
    {
      label: 'Align Left',
      action: onAlignLeft,
      disabled: !hasSelection,
    },
    {
      label: 'Align Right',
      action: onAlignRight,
      disabled: !hasSelection,
    },
    {
      label: 'Align Top',
      action: onAlignTop,
      disabled: !hasSelection,
    },
    {
      label: 'Align Bottom',
      action: onAlignBottom,
      disabled: !hasSelection,
    },
    { type: 'separator' },
    {
      label: 'Distribute Horizontally',
      action: onDistributeHorizontally,
      disabled: !hasSelection,
    },
    {
      label: 'Distribute Vertically',
      action: onDistributeVertically,
      disabled: !hasSelection,
    },
  ]

  // Help Menu
  const helpMenuItems: MenuItemConfig[] = [
    {
      label: 'Documentation',
      shortcut: 'F1',
      action: onShowDocumentation,
      icon: '📖',
    },
    {
      label: 'Keyboard Shortcuts',
      shortcut: 'Ctrl+/',
      action: onShowKeyboardShortcuts,
      icon: '⌨️',
    },
    { type: 'separator' },
    {
      label: 'About',
      action: onShowAbout,
      icon: 'ℹ️',
    },
  ]

  return (
    <div className="menu-bar" role="menubar">
      <Menu
        label="File"
        items={fileMenuItems}
        isOpen={openMenu === 'file'}
        onToggle={() => handleToggleMenu('file')}
        onClose={handleCloseMenu}
      />
      <Menu
        label="Edit"
        items={editMenuItems}
        isOpen={openMenu === 'edit'}
        onToggle={() => handleToggleMenu('edit')}
        onClose={handleCloseMenu}
      />
      <Menu
        label="View"
        items={viewMenuItems}
        isOpen={openMenu === 'view'}
        onToggle={() => handleToggleMenu('view')}
        onClose={handleCloseMenu}
      />
      <Menu
        label="Layout"
        items={layoutMenuItems}
        isOpen={openMenu === 'layout'}
        onToggle={() => handleToggleMenu('layout')}
        onClose={handleCloseMenu}
      />
      <Menu
        label="Help"
        items={helpMenuItems}
        isOpen={openMenu === 'help'}
        onToggle={() => handleToggleMenu('help')}
        onClose={handleCloseMenu}
      />
    </div>
  )
}

export default memo(MenuBar)
